import { Fade, Skeleton, Stack, styled, useMediaQuery } from '@mui/material'
import useAutocomplete from '@mui/material/useAutocomplete'
import type { AutocompleteChangeReason, AutocompleteInputChangeReason } from '@mui/material/useAutocomplete'
import { useForkRef } from '@mui/material/utils'
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { forwardRef, ReactNode, SyntheticEvent, useEffect, useId, useRef, useState } from 'react'

export type SearchBarProps = {
    'aria-label'?: string
    'aria-labelledby'?: string
    className?: string
    value: string
    onChange: (value: string) => void
    clearAriaLabel?: string
    disabled?: boolean
    id?: string
    label?: string
    placeholder?: string
    suggestions?: readonly SearchBarSuggestion[]
    onSuggestionSelect?: (suggestion: SearchBarSuggestion) => void
    suggestionsDescription?: string | ((value: string) => string)
    suggestionsTitle?: string
    suggestionsLoading?: boolean
    suggestionsHideAtLength?: number
    renderSuggestion?: (suggestion: SearchBarSuggestion) => ReactNode
    onSuggestionsOpenChange?: (open: boolean) => void
}

export type SearchBarSuggestion = {
    id: string
    label: string
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
    (
        {
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledBy,
            className,
            clearAriaLabel = 'Suche leeren',
            disabled,
            id,
            label,
            onChange,
            onSuggestionSelect,
            placeholder,
            suggestions,
            suggestionsDescription,
            suggestionsTitle,
            suggestionsLoading = false,
            suggestionsHideAtLength,
            renderSuggestion,
            onSuggestionsOpenChange,
            value,
        },
        ref
    ) => {
        const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
        const generatedId = useId()
        const inputId = id ?? generatedId
        const inputRef = useRef<HTMLInputElement>(null)
        const hasSuggestions = suggestions !== undefined
        const [open, setOpen] = useState(false)
        const belowSuggestionThreshold = suggestionsHideAtLength === undefined || value.trim().length < suggestionsHideAtLength
        const showRecentContent = suggestionsHideAtLength === undefined || !value.trim()
        const description = typeof suggestionsDescription === 'function' ? suggestionsDescription(value) : suggestionsDescription
        const showSuggestionsLoading = showRecentContent && suggestionsLoading
        const visibleSuggestions = showRecentContent && !suggestionsLoading ? (suggestions ?? []) : []
        const handleSuggestionSelection = (
            _event: SyntheticEvent,
            nextValue: SearchBarSuggestion | string | null,
            reason: AutocompleteChangeReason
        ) => {
            // Option selection is separate from text editing so consumers can handle both interactions independently.
            if (reason === 'selectOption' && nextValue && typeof nextValue !== 'string') {
                onSuggestionSelect?.(nextValue)
            }
        }

        const handleInputValueChange = (_event: SyntheticEvent, nextValue: string, reason: AutocompleteInputChangeReason) => {
            // Only propagate user-entered and cleared text; MUI also reports internal resets through this callback.
            if (reason === 'input' || reason === 'clear') {
                onChange(nextValue)
            }
        }

        const {
            focused,
            getClearProps,
            getInputProps,
            getListboxProps,
            getOptionProps,
            getRootProps,
            groupedOptions,
            popupOpen,
            setAnchorEl,
        } = useAutocomplete<SearchBarSuggestion, false, false, true>({
            clearOnBlur: false,
            disabled,
            freeSolo: true,
            filterOptions: suggestionsHideAtLength === undefined ? undefined : (options) => options,
            getOptionLabel: (suggestion) => (typeof suggestion === 'string' ? suggestion : suggestion.label),
            getOptionKey: (suggestion) => (typeof suggestion === 'string' ? suggestion : suggestion.id),
            id: inputId,
            inputValue: value,
            onChange: handleSuggestionSelection,
            onInputChange: handleInputValueChange,
            openOnFocus: true,
            open,
            onOpen: () => setOpen(true),
            onClose: (event, reason) => {
                if (
                    reason === 'blur' &&
                    event &&
                    'relatedTarget' in event &&
                    event.relatedTarget instanceof Node &&
                    inputRef.current?.closest('[data-search-bar-root]')?.contains(event.relatedTarget)
                ) {
                    return
                }
                setOpen(false)
            },
            options: visibleSuggestions,
            value: null,
        })
        const { ref: autocompleteInputRef, ...inputPropsFromAutocomplete } = getInputProps()
        // MUI uses its ref for autocomplete state; our ref supports clear/focus behavior, so both (and the consumer ref) target the same input.
        const inputRefs = useForkRef(autocompleteInputRef, inputRef, ref)
        const clearProps = getClearProps()
        const listboxProps = getListboxProps()
        const hasVisibleSuggestions = groupedOptions.length > 0
        const hasSuggestionContent = hasVisibleSuggestions || showSuggestionsLoading
        const isSuggestionListOpen = Boolean(
            !disabled && hasSuggestions && popupOpen && belowSuggestionThreshold && (hasSuggestionContent || description)
        )
        useEffect(() => {
            onSuggestionsOpenChange?.(isSuggestionListOpen)
        }, [isSuggestionListOpen, onSuggestionsOpenChange])
        const isLabelFloating = Boolean(label && (focused || open || value))
        const rootProps = getRootProps({ className })

        return (
            <SearchBarRoot
                {...rootProps}
                data-search-bar-root
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setOpen(false)
                    }
                }}
                onKeyDown={(event) => {
                    if (event.target instanceof HTMLButtonElement) {
                        if (event.key === 'Escape') {
                            inputRef.current?.focus()
                            setOpen(false)
                        }
                        return
                    }
                    if (hasSuggestions) {
                        rootProps.onKeyDown?.(event)
                    }
                }}
                ref={setAnchorEl}
            >
                {label && isLabelFloating && (
                    <SearchLabel htmlFor={inputId} $disabled={disabled} $floating>
                        {label}
                    </SearchLabel>
                )}
                <InputShell data-search-bar-input>
                    <InputContent>
                        {label && !isLabelFloating && (
                            <SearchLabel htmlFor={inputId} $disabled={disabled} $floating={false}>
                                {label}
                            </SearchLabel>
                        )}
                        <SearchInput
                            {...inputPropsFromAutocomplete}
                            aria-label={ariaLabel}
                            aria-labelledby={ariaLabelledBy}
                            disabled={disabled}
                            id={inputId}
                            placeholder={label && !isLabelFloating ? undefined : placeholder}
                            ref={inputRefs}
                            role={hasSuggestions ? 'combobox' : 'searchbox'}
                            type="search"
                            value={value}
                            aria-autocomplete={hasSuggestions ? 'list' : undefined}
                            aria-controls={isSuggestionListOpen && hasVisibleSuggestions ? listboxProps.id : undefined}
                            aria-expanded={hasSuggestions ? isSuggestionListOpen : undefined}
                            onKeyDown={(event) => {
                                const hasHighlightedSuggestion = Boolean(inputRef.current?.getAttribute('aria-activedescendant'))
                                if (event.key === 'Enter' && isSuggestionListOpen && !hasHighlightedSuggestion) {
                                    event.preventDefault()
                                    event.stopPropagation()
                                }
                            }}
                        />
                    </InputContent>
                    <IconWrapper>
                        {value && !disabled && (
                            <ClearButton
                                {...clearProps}
                                type="button"
                                aria-label={clearAriaLabel}
                                onPointerDown={(event) => event.preventDefault()}
                                onClick={(event) => {
                                    clearProps.onClick?.(event)
                                    inputRef.current?.focus()
                                }}
                            >
                                <XIcon aria-hidden weight="bold" size={tokens.SizingIconS} color={tokens.ColorIconBoldest} />
                            </ClearButton>
                        )}
                        <MagnifyingGlassIcon
                            aria-hidden
                            weight="bold"
                            size={tokens.SizingIconS}
                            color={disabled ? tokens.ColorIconDisabled : tokens.ColorIconSubtle}
                        />
                    </IconWrapper>
                </InputShell>
                <Fade
                    in={isSuggestionListOpen}
                    mountOnEnter
                    unmountOnExit
                    timeout={reduceMotion ? 0 : { enter: 120, exit: 80 }}
                    easing={{ enter: 'ease-out', exit: 'ease-in' }}
                    onEnter={(node) => {
                        node.style.transitionProperty = 'opacity, transform'
                        node.inert = false
                        node.removeAttribute('aria-hidden')
                    }}
                    onEntering={(node) => {
                        node.style.transform = 'translateY(0)'
                    }}
                    onExit={(node) => {
                        node.style.transitionProperty = 'opacity, transform'
                        node.style.transform = reduceMotion ? 'none' : `translateY(-${tokens.SizingSpacing050})`
                        node.inert = true
                        node.setAttribute('aria-hidden', 'true')
                    }}
                >
                    <SuggestionPopover $rich={!!renderSuggestion && hasSuggestionContent}>
                        {description && <SuggestionHeader>{description}</SuggestionHeader>}
                        {hasSuggestionContent && suggestionsTitle && <SuggestionTitle>{suggestionsTitle}</SuggestionTitle>}
                        {showSuggestionsLoading && <SuggestionSkeletons rich={!!renderSuggestion} />}
                        {hasVisibleSuggestions && (
                            <SuggestionList {...listboxProps} $rich={!!renderSuggestion}>
                                {groupedOptions.map((suggestion, index) => {
                                    const { key, ...optionProps } = getOptionProps({ option: suggestion, index })
                                    return (
                                        <Suggestion {...optionProps} $rich={!!renderSuggestion} key={key}>
                                            {renderSuggestion ? renderSuggestion(suggestion) : suggestion.label}
                                        </Suggestion>
                                    )
                                })}
                            </SuggestionList>
                        )}
                    </SuggestionPopover>
                </Fade>
            </SearchBarRoot>
        )
    }
)

SearchBar.displayName = 'SearchBar'

function SuggestionSkeletons({ rich }: { rich: boolean }) {
    return (
        <SuggestionList as="div" $rich={rich} role="status" aria-label="Vorschläge werden geladen">
            {Array.from({ length: 3 }, (_, index) => (
                <Stack
                    key={index}
                    aria-hidden
                    direction="row"
                    alignItems="center"
                    gap={tokens.SizingSpacing150}
                    sx={{
                        minHeight: tokens.SizingSpacing600,
                        padding: `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`,
                        boxSizing: 'border-box',
                    }}
                >
                    {rich && (
                        <Skeleton
                            variant="rounded"
                            width={tokens.SizingSpacing600}
                            height="auto"
                            sx={{ aspectRatio: '76 / 59', flexShrink: 0 }}
                        />
                    )}
                    <Stack flex={1} minWidth={0}>
                        <Skeleton width="80%" height={tokens.TextS.lineHeight} />
                        {rich && <Skeleton width="45%" height={tokens.TextXs.lineHeight} />}
                    </Stack>
                    {rich && (
                        <Skeleton
                            variant="rounded"
                            width={tokens.SizingSpacing1000}
                            height={tokens.TextS.lineHeight}
                            sx={{ flexShrink: 0 }}
                        />
                    )}
                </Stack>
            ))}
        </SuggestionList>
    )
}

const SearchBarRoot = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.SizingSpacing050,
    width: '100%',
})

const InputShell = styled('div')({
    height: '46px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `0 ${tokens.SizingSpacing150}`,
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerS,
    backgroundColor: tokens.ColorSurfaceDefault,
    boxSizing: 'border-box',
    '&:hover': {
        backgroundColor: tokens.ColorSurfaceHover,
    },
    '&:focus-within': {
        borderColor: tokens.ColorBorderFocused,
        backgroundColor: tokens.ColorSurfaceDefault,
    },
    '&:active': {
        backgroundColor: tokens.ColorSurfacePressed,
    },
    '&:has(input:disabled)': {
        borderColor: tokens.ColorBorderDisabled,
        backgroundColor: tokens.ColorSurfaceDisabled,
    },
})

const InputContent = styled('div')({
    display: 'flex',
    flex: '1 0 0',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
})

const SearchLabel = styled('label', {
    shouldForwardProp: (prop) => prop !== '$disabled' && prop !== '$floating',
})<{ $disabled?: boolean; $floating: boolean }>(({ $disabled, $floating }) => {
    const floatingBackground = $disabled ? tokens.ColorSurfaceDisabled : tokens.ColorSurfaceDefault

    return {
        ...($floating ? tokens.TextXs : tokens.TextS),
        position: 'absolute',
        zIndex: 1,
        left: $floating ? tokens.SizingSpacing075 : 0,
        top: $floating ? 0 : '50%',
        height: $floating ? tokens.SizingBorderM : undefined,
        display: $floating ? 'flex' : undefined,
        alignItems: $floating ? 'center' : undefined,
        justifyContent: $floating ? 'center' : undefined,
        padding: $floating ? `0 ${tokens.SizingSpacing075}` : 0,
        color: $disabled ? tokens.ColorTextDisabled : tokens.ColorTextSubtle,
        backgroundColor: $floating ? floatingBackground : 'transparent',
        pointerEvents: 'none',
        transform: $floating ? undefined : 'translateY(-50%)',
        whiteSpace: 'nowrap',
    }
})

const SearchInput = styled('input')({
    ...tokens.TextS,
    minWidth: 0,
    width: '100%',
    padding: 0,
    border: 0,
    outline: 0,
    color: tokens.ColorTextBold,
    caretColor: tokens.ColorIconBoldest,
    backgroundColor: 'transparent',
    textOverflow: 'ellipsis',
    '&::placeholder': {
        color: tokens.ColorTextSubtle,
        opacity: 1,
    },
    '&:disabled': {
        color: tokens.ColorTextDisabled,
    },
    '&:disabled::placeholder': {
        color: tokens.ColorTextDisabled,
    },
    '&::-webkit-search-cancel-button': {
        appearance: 'none',
    },
})

const IconWrapper = styled('div')({
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    gap: tokens.SizingSpacing050,
    paddingLeft: tokens.SizingSpacing100,
})

const ClearButton = styled('button')({
    width: tokens.SizingIconS,
    height: tokens.SizingIconS,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 0,
    color: tokens.ColorIconBoldest,
    backgroundColor: 'transparent',
    cursor: 'pointer',
})

const SuggestionPopover = styled('div', { shouldForwardProp: (prop) => prop !== '$rich' })<{ $rich: boolean }>(({ theme, $rich }) => ({
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: theme.zIndex.modal,
    width: $rich ? 'max(100%, 580px)' : '100%',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'min(640px, 70vh)',
    padding: `${tokens.SizingSpacing050} 0 ${tokens.SizingSpacing100}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: tokens.SizingCornerS,
    backgroundColor: tokens.ColorSurfaceDefault,
    boxShadow: tokens.Flyout.boxShadow,
    transform: `translateY(-${tokens.SizingSpacing050})`,
    '@media (prefers-reduced-motion: reduce)': {
        transform: 'none',
    },
}))

const SuggestionTitle = styled('h5')({
    ...tokens.Headline5,
    margin: 0,
    minHeight: tokens.SizingSpacing400,
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${tokens.SizingSpacing150}`,
    color: tokens.ColorTextSubtle,
    flexShrink: 0,
})

const SuggestionHeader = styled('div')({
    ...tokens.TextS,
    minHeight: tokens.SizingSpacing400,
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${tokens.SizingSpacing150}`,
    color: tokens.ColorTextSubtle,
    flexShrink: 0,
})

const SuggestionList = styled('ul', { shouldForwardProp: (prop) => prop !== '$rich' })<{ $rich: boolean }>(({ $rich }) => ({
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    padding: $rich ? `0 ${tokens.SizingSpacing100}` : 0,
    overflowY: 'auto',
    listStyle: 'none',
}))

const Suggestion = styled('li', { shouldForwardProp: (prop) => prop !== '$rich' })<{ $rich: boolean }>(({ $rich }) => ({
    ...tokens.TextS,
    minHeight: $rich ? tokens.SizingSpacing600 : tokens.SizingSpacing400,
    boxSizing: $rich ? 'border-box' : undefined,
    borderRadius: $rich ? tokens.SizingCornerM : 0,
    display: 'flex',
    alignItems: 'center',
    padding: $rich ? `${tokens.SizingSpacing075} ${tokens.SizingSpacing100}` : tokens.SizingSpacing150,
    color: tokens.ColorTextBold,
    backgroundColor: tokens.ColorSurfaceDefault,
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.ColorSurfaceHover },
    '&.Mui-focusVisible': { backgroundColor: tokens.ColorSurfaceHover },
}))
